import { signPieceUrl } from "../api/client";
import type { SignAsk, SignDoc, SignParty, SignPiece } from "../types";
import { IcoClose, IcoDownload } from "./icons";

function rankLabel(i: number): string {
  if (i === 0) return "1re partie";
  return `${i + 1}e partie`;
}

function PieceAsk({
  docId,
  ask,
  piece,
}: {
  docId: number;
  ask: SignAsk;
  piece?: SignPiece;
}) {
  return (
    <li>
      <strong>{ask.label}</strong>
      <span>{ask.required ? "Obligatoire" : "Facultatif"}</span>
      {piece ? (
        <button type="button" className="sg-btn ghost" onClick={() => window.open(signPieceUrl(docId, piece.id), "_blank")}>
          <IcoDownload />
          {piece.filename}
        </button>
      ) : (
        <em>Pas encore déposée</em>
      )}
    </li>
  );
}

export function PiecesSheet({ doc, onClose }: { doc: SignDoc; onClose: () => void }) {
  const asks = doc.asks ?? [];
  const pieces = doc.pieces ?? [];
  const parties = (doc.parties ?? []).slice().sort((a, b) => a.sort_order - b.sort_order);
  const groups: { party: SignParty | null; label: string; items: SignPiece[] }[] =
    parties.length > 0
      ? parties.map((party, i) => ({
          party,
          label: `${rankLabel(i)}${party.name ? ` · ${party.name}` : ""}`,
          items: pieces.filter((p) => p.party_id === party.id),
        }))
      : [{ party: null, label: "Pièces", items: pieces }];
  const orphan = pieces.filter((p) => parties.length > 0 && !parties.some((x) => x.id === p.party_id));

  return (
    <div className="sg-send-back" role="dialog" aria-label="Pièces du dossier">
      <div className="sg-send is-wide">
        <h2>Pièces du dossier</h2>
        <p style={{ color: "var(--sg-muted)", margin: "0.2rem 0 0" }}>{doc.title}</p>
        {asks.length === 0 && pieces.length === 0 ? (
          <p className="sg-legal">Aucune pièce demandée sur cet envoi.</p>
        ) : (
          groups.map((g) => (
            <section key={g.party?.id ?? "all"} className="sg-pieces-block">
              <h3>{g.label}</h3>
              <ul className="sg-pieces">
                {asks.length > 0
                  ? asks.map((a) => (
                      <PieceAsk
                        key={`${g.party?.id ?? "x"}-${a.id ?? a.label}`}
                        docId={doc.id}
                        ask={a}
                        piece={g.items.find((p) => p.ask_id === a.id)}
                      />
                    ))
                  : g.items.map((p) => (
                      <li key={p.id}>
                        <strong>{p.ask_label || p.filename}</strong>
                        <button type="button" className="sg-btn ghost" onClick={() => window.open(signPieceUrl(doc.id, p.id), "_blank")}>
                          <IcoDownload />
                          {p.filename}
                        </button>
                      </li>
                    ))}
              </ul>
            </section>
          ))
        )}
        {orphan.length > 0 ? (
          <section className="sg-pieces-block">
            <h3>Autres</h3>
            <ul className="sg-pieces">
              {orphan.map((p) => (
                <li key={p.id}>
                  <strong>{p.ask_label || p.filename}</strong>
                  <button type="button" className="sg-btn ghost" onClick={() => window.open(signPieceUrl(doc.id, p.id), "_blank")}>
                    <IcoDownload />
                    {p.filename}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        <div className="sg-send__actions">
          <button type="button" className="sg-btn ghost" onClick={onClose}>
            <IcoClose />
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
